import { HTTPMethod } from '@/type-utils/HTTPMethod';
import config from '../config';

/** Configuration for the SFCC services usable by DECA. */
const sfccServices = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      category: {
        methods: {
          /**
           * Retrieve a single category by ID.
           */
          getCategory: {
            endpoint:
              'product/shopper-products/v1/organizations/{organizationID}/categories/{id}',
            httpMethod: HTTPMethod.Get
          },
          /**
           * Retrieve products for a specific category.
           */
          getCategoryProducts: {
            endpoint:
              'search/shopper-search/v1/organizations/{organizationID}/product-search',
            httpMethod: HTTPMethod.Get
          },
          /**
           * Resolve URL mapping for a path segment.
           */
          getUrlMapping: {
            endpoint:
              'site/shopper-seo/v1/organizations/{organizationID}/url-mapping',
            httpMethod: HTTPMethod.Get
          }
        }
      },
      customObject: {
        methods: {
          /**
           * Fetch a custom object.
           */
          getCustomObject: {
            endpoint:
              'custom-object/shopper-custom-objects/v1/organizations/{organizationID}/custom-objects/{objectType}/{key}',
            httpMethod: HTTPMethod.Get
          }
        }
      },
      customProduct: {
        methods: {
          /**
           * Retrieve one or more products using the custom product API. Returns
           * pre-formatted product data with additional data needed for parsing
           * such as images, categories, features, variants, supergroups and
           * swatch metadata.
           *
           * Requires siteId and c_id (comma-separated product IDs) query
           * parameters.
           */
          getFullProducts: {
            endpoint:
              'custom/product/v1/organizations/{organizationID}/full-products',
            httpMethod: HTTPMethod.Get
          }
        }
      },
      navigation: {
        methods: {
          /**
           * Retrieve navigational data.
           */
          getNavigationalData: {
            endpoint:
              'product/shopper-products/v1/organizations/{organizationID}/categories',
            httpMethod: HTTPMethod.Get
          }
        }
      },
      product: {
        methods: {
          /**
           * Retrieve a single product.
           */
          getProduct: {
            endpoint:
              'product/shopper-products/v1/organizations/{organizationID}/products/{id}',
            httpMethod: HTTPMethod.Get
          },
          /**
           * Retrieve multiple products.
           */
          getProducts: {
            endpoint:
              'product/shopper-products/v1/organizations/{organizationID}/products',
            httpMethod: HTTPMethod.Get
          }
        }
      },
      shippingMethod: {
        methods: {
          /**
           * Retrieve all shipping methods for a site.
           */
          getShippingMethods: {
            endpoint:
              'custom/shipping-methods/v1/organizations/{organizationID}/shipping-methods',
            httpMethod: HTTPMethod.Get
          },
          /**
           * Retrieve a single shipping method by ID.
           */
          getShippingMethod: {
            endpoint:
              'custom/shipping-methods/v1/organizations/{organizationID}/shipping-methods/{shippingMethodId}',
            httpMethod: HTTPMethod.Get
          }
        }
      },
      customer: {
        methods: {
          /**
           * Register a new customer. Requires a guest JWT.
           */
          registerCustomer: {
            endpoint:
              'customer/shopper-customers/v1/organizations/{organizationID}/customers',
            httpMethod: HTTPMethod.Post
          },
          /**
           * Retrieve a customer by ID. Requires a registered JWT.
           */
          getCustomer: {
            endpoint:
              'customer/shopper-customers/v1/organizations/{organizationID}/customers/{customerId}',
            httpMethod: HTTPMethod.Get
          },
          /**
           * Update a customer's profile. Requires a registered JWT.
           */
          updateCustomer: {
            endpoint:
              'customer/shopper-customers/v1/organizations/{organizationID}/customers/{customerId}',
            httpMethod: HTTPMethod.Patch
          },
          /**
           * Update a customer's password. Requires current and new password.
           */
          updateCustomerPassword: {
            endpoint:
              'customer/shopper-customers/v1/organizations/{organizationID}/customers/{customerId}/password',
            httpMethod: HTTPMethod.Put
          },
          /**
           * Create a new address for a customer.
           */
          createCustomerAddress: {
            endpoint:
              'customer/shopper-customers/v1/organizations/{organizationID}/customers/{customerId}/addresses',
            httpMethod: HTTPMethod.Post
          },
          /**
           * Retrieve all addresses for a customer.
           */
          getCustomerAddresses: {
            endpoint:
              'customer/shopper-customers/v1/organizations/{organizationID}/customers/{customerId}/addresses',
            httpMethod: HTTPMethod.Get
          },
          /**
           * Update a customer's address by address name.
           */
          updateCustomerAddress: {
            endpoint:
              'customer/shopper-customers/v1/organizations/{organizationID}/customers/{customerId}/addresses/{addressName}',
            httpMethod: HTTPMethod.Patch
          },
          /**
           * Delete a customer's address by address name.
           */
          deleteCustomerAddress: {
            endpoint:
              'customer/shopper-customers/v1/organizations/{organizationID}/customers/{customerId}/addresses/{addressName}',
            httpMethod: HTTPMethod.Delete
          }
        }
      }
    }
  }
});

export default sfccServices;
